import React, { useState, useMemo } from "react";
import { Button } from "react-md";
import ZypleRadio from "../../../../../../commonComponents/ZypleRadio";
import InfoButton from "../../../../account-module/helper/InfoButton";
import WalletRulesInfoBox from "./WalletRulesInfoBox";
import { formatCurrency } from "../../../helper/formatCurrency";
import { SCREEN_STATE, PAYMENT_METHOD } from "../paymentLinkConstants";

const SelectPaymentMethod = ({
  paymentMethod,
  setPaymentMethod,
  goNext,
  walletSubLimit,
  withCreateAppointment,
}) => {
  const [showWalletRules, setShowWalletRules] = useState(false);

  const sublimit = walletSubLimit;
  const isWalletIncluded = sublimit?.isWalletIncluded;
  const isUnlimited = sublimit?.isUnlimitedWallet;
  const useZylaWallet = sublimit?.useZylaWallet;
  const useExternalWallet = sublimit?.useExternalWallet;

  const available = sublimit?.zylaWallet?.available ?? 0;
  const grandTotal = sublimit?.grandTotal ?? 0;

  const isWalletAndRazorpayTooSmall =
    useExternalWallet > 0 && useExternalWallet < 1;

  const isRazorpayOnlyTooSmall = grandTotal > 0 && grandTotal < 1;

  const validOptions = useMemo(() => {
    if (withCreateAppointment) {
      if (!isWalletIncluded) return [PAYMENT_METHOD.RAZORPAY_ONLY]; // Only Razorpay

      if (isUnlimited && sublimit?.useZylaWallet === grandTotal)
        return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_ONLY]; // Full wallet + Razorpay

      // Limited wallet
      if (useZylaWallet == grandTotal)
        return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_ONLY]; // Wallet fully covers
      if (useZylaWallet > 0 && useZylaWallet < grandTotal)
        return [
          PAYMENT_METHOD.WALLET_AND_RAZORPAY,
          PAYMENT_METHOD.RAZORPAY_ONLY,
        ]; // Partial wallet + razor
      if (available <= 0 && useZylaWallet <= 0)
        return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_DISABLED]; // Wallet empty - so only 2 availabe to select

      return [PAYMENT_METHOD.RAZORPAY_ONLY]; // fallback
    } else {
      if (!isWalletIncluded) return [PAYMENT_METHOD.RAZORPAY_ONLY]; // Only Razorpay

      if (isUnlimited && sublimit?.useZylaWallet === grandTotal)
        return [PAYMENT_METHOD.WALLET_ONLY]; // Only Wallet

      // Limited wallet
      if (useZylaWallet == grandTotal) return [PAYMENT_METHOD.WALLET_ONLY]; // Wallet fully covers
      if (useExternalWallet == grandTotal)
        return [PAYMENT_METHOD.RAZORPAY_ONLY]; // Only Razorpay
      if (useZylaWallet > 0 && useZylaWallet < grandTotal)
        return [PAYMENT_METHOD.WALLET_AND_RAZORPAY]; // Partial wallet + razor
    }
  }, [isWalletIncluded, isUnlimited, available, grandTotal]);

  // Reset selection if selected option becomes invalid
  React.useEffect(() => {
    if (paymentMethod && validOptions?.includes(paymentMethod)) return;

    const firstValidOption = validOptions?.find((option) => {
      if (
        option === PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
        isWalletAndRazorpayTooSmall
      ) {
        return false;
      }

      if (option === PAYMENT_METHOD.RAZORPAY_ONLY && isRazorpayOnlyTooSmall) {
        return false;
      }

      return true;
    });

    if (firstValidOption) {
      setPaymentMethod(firstValidOption);
    } else {
      setPaymentMethod(-1); // nothing valid
    }
  }, [validOptions, isWalletAndRazorpayTooSmall, isRazorpayOnlyTooSmall]);

  return (
    <div className="pld-screen pld-method-container">
      <span className="pld-title">Choose a payment method:</span>

      {/* OPTION 1 - Wallet + Razorpay */}
      {validOptions?.includes(PAYMENT_METHOD.WALLET_AND_RAZORPAY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === PAYMENT_METHOD.WALLET_AND_RAZORPAY}
              onChange={() =>
                setPaymentMethod(PAYMENT_METHOD.WALLET_AND_RAZORPAY)
              }
              value={PAYMENT_METHOD.WALLET_AND_RAZORPAY}
              name="pay-radio"
            />
            <span className="pld-option-text">
              Pay via Wallet and Razorpay payment link
            </span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(useZylaWallet)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <WalletRulesInfoBox transactionData={sublimit} />
            )}
            <div>
              • via Razorpay:{" "}
              <span className="pld-bold">
                {formatCurrency(useExternalWallet)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OPTION 4 - Wallet Disabled */}
      {validOptions?.includes(PAYMENT_METHOD.WALLET_DISABLED) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === PAYMENT_METHOD.WALLET_DISABLED}
              onChange={() => setPaymentMethod(PAYMENT_METHOD.WALLET_DISABLED)}
              value={PAYMENT_METHOD.WALLET_DISABLED}
              name="pay-radio"
              disabled
            />
            <span className="pld-option-text-disabled">Pay via Wallet</span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center-disabled">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(0)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <WalletRulesInfoBox transactionData={sublimit} />
            )}
          </div>
        </div>
      )}

      {/* OPTION 2 - Razorpay Only */}
      {validOptions?.includes(PAYMENT_METHOD.RAZORPAY_ONLY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === PAYMENT_METHOD.RAZORPAY_ONLY}
              onChange={() => setPaymentMethod(PAYMENT_METHOD.RAZORPAY_ONLY)}
              value={PAYMENT_METHOD.RAZORPAY_ONLY}
              name="pay-radio"
            />
            <span className="pld-option-text">
              Pay via Razorpay payment link
            </span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Razorpay:
              <span className="pld-bold margin-left-4">
                {formatCurrency(grandTotal)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OPTION 3 - Wallet Only */}
      {validOptions?.includes(PAYMENT_METHOD.WALLET_ONLY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === PAYMENT_METHOD.WALLET_ONLY}
              onChange={() => setPaymentMethod(PAYMENT_METHOD.WALLET_ONLY)}
              value={PAYMENT_METHOD.WALLET_ONLY}
              name="pay-radio"
            />
            <span className="pld-option-text">Pay via Wallet</span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(grandTotal)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <WalletRulesInfoBox transactionData={sublimit} />
            )}
          </div>
        </div>
      )}

      {/* NOTE BOX */}
      <div className="pld-note-container">
        <div className="pld-note-box">
          <span className="pld-bold">Note:&nbsp;</span>

          {(paymentMethod === PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
            isWalletAndRazorpayTooSmall) ||
          (paymentMethod === PAYMENT_METHOD.RAZORPAY_ONLY &&
            isRazorpayOnlyTooSmall) ? (
            <>
              You can't proceed with this payment method as payment of less than
              Rs 1 is not allowed with Razorpay.
            </>
          ) : paymentMethod === PAYMENT_METHOD.WALLET_ONLY ? (
            <>
              Once you click on continue, amount will instantly deduct from
              wallet
            </>
          ) : (
            <>
              The selected payment preferences cannot be modified after clicking
              'Continue'
            </>
          )}
        </div>
      </div>

      {/* BUTTON */}
      <div className="pld-center">
        <Button
          raised
          className="zy-btn-orange pld-main-btn"
          disabled={
            !paymentMethod ||
            paymentMethod === -1 ||
            paymentMethod === PAYMENT_METHOD.WALLET_DISABLED ||
            (paymentMethod === PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
              isWalletAndRazorpayTooSmall) ||
            (paymentMethod === PAYMENT_METHOD.RAZORPAY_ONLY &&
              isRazorpayOnlyTooSmall)
          }
          onClick={() => {
            if (
              !paymentMethod ||
              paymentMethod === PAYMENT_METHOD.WALLET_DISABLED
            ) {
              return;
            }
            goNext(
              paymentMethod === PAYMENT_METHOD.WALLET_ONLY
                ? SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET
                : SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS,
            );
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default SelectPaymentMethod;
